import { Texture } from "@/cache/Texture";
import type { GridDimension, GridPayload } from "@mm/api.meteomatics.com";
import { InterpolationMode } from "@mm/weather-parameter-utils";
import type { Tile } from "../Tile";

export interface TileTextureData extends GridPayload<Float32Array>, GridDimension {
  interpolationMode: InterpolationMode;
}

function isPowerOf2(v: number): boolean {
  return !!v && !(v & (v - 1));
}

/**
 * Data for a tile without information about the tiles position. See `PositionedTile` and `TileGeometry`.
 *
 * Beware: The tile is upside down (north is on the bottom, south is on the top), because that is
 * how we receive it from the backend.
 */
export class BinaryTile extends Texture implements Tile {
  /**
   * Create a new tile
   *
   * @param data The data that will be uploaded to the GPU
   */
  constructor(readonly data: TileTextureData) {
    super();
    if (!isPowerOf2(data.width) || !isPowerOf2(data.height)) {
      throw Error(`binary tile texture size has to be a power of two, but got ${data.width}x${data.height}`);
    }
  }

  memoryConsumptionCpu(): number {
    return this.data.estimatedByteSize;
  }

  memoryConsumptionGpu(): number {
    let mipmappingOverhead = 1.0;

    const interpolationMode: InterpolationMode = this.data.interpolationMode;
    switch (interpolationMode) {
      case InterpolationMode.IntervalOrRatio_NonLinear:
      case InterpolationMode.NominalOrOrdinal_NearestNeighbour:
        break;
      case InterpolationMode.IntervalOrRatio_Linear:
        mipmappingOverhead = 1.34;
        break;
      default: {
        const _exhaustive: never = interpolationMode;
        return _exhaustive;
      }
    }

    const numberOfTimesUploaded = this._textures.size;

    return this.data.estimatedByteSize * mipmappingOverhead * numberOfTimesUploaded;
  }

  protected upload(gl: WebGL2RenderingContext, texture: WebGLTexture) {
    const level = 0;
    const internalFormat = gl.R32F;
    const srcFormat = gl.RED;
    const srcType = gl.FLOAT;
    const border = 0;
    gl.texImage2D(
      gl.TEXTURE_2D,
      level,
      internalFormat,
      this.data.width,
      this.data.height,
      border,
      srcFormat,
      srcType,
      this.data.payload,
    );

    // our mapping is not 100% precise. A default clamping strategy of gl.REPEAT can result in discolorations at tile edges.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    // Param Setting 	Linear within mip-level 	Has mipmapping 	Linear between mip-levels
    // GL_NEAREST 	No 	No
    // GL_LINEAR 	Yes 	No
    // GL_NEAREST_MIPMAP_NEAREST 	No 	Yes 	No
    // GL_LINEAR_MIPMAP_NEAREST 	Yes 	Yes 	No
    // GL_NEAREST_MIPMAP_LINEAR 	No 	Yes 	Yes
    // GL_LINEAR_MIPMAP_LINEAR 	Yes 	Yes 	Yes
    const interpolationMode: InterpolationMode = this.data.interpolationMode;
    switch (interpolationMode) {
      case InterpolationMode.IntervalOrRatio_NonLinear:
      case InterpolationMode.NominalOrOrdinal_NearestNeighbour:
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        break;
      case InterpolationMode.IntervalOrRatio_Linear:
        // Fallback to nearest interpolation.
        // https://webgl2fundamentals.org/webgl/lessons/webgl-cross-platform-issues.html
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        break;
      default: {
        const _exhaustive: never = interpolationMode;
        return _exhaustive;
      }
    }
  }
}
